import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

const ContentCacheContext = createContext(null);

// Default TTL: 5 minutes. Per-entry TTLs can be overridden when calling getCached.
export const CACHE_EXPIRATION_TIME = 5 * 60 * 1000;

const buildKey = (scope, key) => `${scope}::${key}`;

export function ContentCacheProvider({ children }) {
    // Single in-memory store keyed by `${scope}::${key}` (e.g. `get-data::user123::2025-01-01`).
    // Each entry: { value, expiresAt }.
    const cacheRef = useRef(new Map());
    // Tick is bumped whenever the cache is mutated so consumers can subscribe to changes.
    const [, setVersion] = useState(0);
    const bumpVersion = useCallback(() => setVersion(v => v + 1), []);

    const read = useCallback((fullKey) => {
        const entry = cacheRef.current.get(fullKey);
        if (!entry) return undefined;
        if (entry.expiresAt > 0 && entry.expiresAt < Date.now()) {
            cacheRef.current.delete(fullKey);
            return undefined;
        }
        return entry.value;
    }, []);

    const write = useCallback((fullKey, value, ttl) => {
        cacheRef.current.set(fullKey, {
            value,
            expiresAt: ttl > 0 ? Date.now() + ttl : 0,
        });
        bumpVersion();
    }, [bumpVersion]);

    // Invalidate everything scoped to a user (e.g. after they record a sale / change a setting).
    const invalidateUser = useCallback((username) => {
        if (!username) return;
        const prefix = `::${username}::`;
        for (const key of cacheRef.current.keys()) {
            if (key.endsWith(`::${username}`) || key.includes(prefix)) {
                cacheRef.current.delete(key);
            }
        }
        bumpVersion();
    }, [bumpVersion]);

    // Invalidate a single cache entry.
    const invalidate = useCallback((scope, key) => {
        cacheRef.current.delete(buildKey(scope, key));
        bumpVersion();
    }, [bumpVersion]);

    /**
     * Generic cache-first fetcher.
     *
     *   const data = await getCached('get-data', `${username}::${startDate}::${endDate}`, fetchFn);
     *
     * - scope: short namespace (e.g. 'get-data', 'offers', 'sales', 'latest-videos').
     * - key:   stable identifier (include username so per-user caches don't collide).
     * - fetcher: async () => freshData. Called only on a cache miss.
     * - ttl:    ms until expiry (defaults to CACHE_EXPIRATION_TIME). Pass 0 for session-only.
     *
     * Returns `{ data, fromCache }`.
     */
    const getCached = useCallback(async (scope, key, fetcher, ttl = CACHE_EXPIRATION_TIME) => {
        const fullKey = buildKey(scope, key);
        const cached = read(fullKey);
        if (cached !== undefined) {
            return { data: cached, fromCache: true };
        }
        const fresh = await fetcher();
        write(fullKey, fresh, ttl);
        return { data: fresh, fromCache: false };
    }, [read, write]);

    const value = useMemo(() => ({
        getCached,
        invalidate,
        invalidateUser,
    }), [getCached, invalidate, invalidateUser]);

    return (
        <ContentCacheContext.Provider value={value}>
            {children}
        </ContentCacheContext.Provider>
    );
}

export function useContentCache() {
    const ctx = useContext(ContentCacheContext);
    if (!ctx) {
        throw new Error('useContentCache must be used within a ContentCacheProvider');
    }
    return ctx;
}